import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SaveAndContinueFormBuilder } from '@/formbuilder/SaveAndContinueFormBuilder.page';
import { ImpactReportSlider } from '@/impactReports/ImpactReportSlider.atom';
import { KpiLabelWithDescription } from '@/impactReports/KpiLabelWithDescription.atom';
import { SelectedProjectDisplayAndLink } from '@/impactReports/SelectedProjectDisplayAndLink.atom';
import { SelectedWorksheetDisplayAndLink } from '@/impactReports/SelectedWorksheetDisplayAndLink.atom';
import { ImpactReportAIOverlay } from '@/impactReports/ValueCaptureAIOverlay.atom';
import {
  DAILY_FREQUENCY,
  MONTHLY_FREQUENCY,
  QUARTERLY_FREQUENCY,
  TRACK_IMPACT,
  WEEKLY_FREQUENCY,
  YEARLY_FREQUENCY,
  availableStages,
  instructions,
  stageIcons,
  toImpactReportSelectedItemType,
} from '@/impactReports/impactReport.types';
import {
  addOrUpdateImpactReport,
  giveEveryoneGroupReadPermissions,
  removeEveryoneGroupReadPermissions,
  resolveImpactReportReviewSave,
  stageDisplayStringByIndex,
  toSavedImpactReportReviewState,
  verifyEveryoneGroupReadPermissions,
  withImpactReportReview,
} from '@/impactReports/impactReport.utilities';
import {
  getCustomCategoryTitle,
  getKPIDisplayName,
  getKPIExpectedAnnualColumnName,
  getKPIUnit,
} from '@/impactReports/impactReportDisplay.utilities';
import {
  changeImpactReportKpiMode,
  loadImpactReportDraft,
  serializeImpactReportDraft,
} from '@/impactReports/impactReportForm.utilities';
import {
  useImpactReportActions,
  useImpactReportExpectedAnnualKpiColumns,
  useImpactReportExpectedAnnualKpiVisibility,
  useImpactReportKpiColumns,
  useImpactReportShowAssetPath,
  useImpactReportShowLocation,
  useImpactReportShowSite,
  useImpactReportTableId,
} from '@/impactReports/impactReports.store';
import { useImpactReportApiActions } from '@/impactReports/useImpactReportApiActions.hook';
import { useImpactReportCategorySave } from '@/impactReports/useImpactReportCategorySave.hook';
import { ReportContentWorksheetAndUrl } from '@/reportEditor/components/reportContentModal/shared/ReportContentWorksheetAndUrl.molecule';
import { hasAnalyticsAdminCapability } from '@/services/authorization.service';
import { genAIEnabled } from '@/services/systemConfiguration.utilities';
import { doTrack } from '@/track/track.service';
import { randomUUID } from '@/utilities/guid.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { SelectWorksheet } from '@/worksheets/SelectWorksheet.molecule';
import { sqWorkbooksApi } from '@seeqdev/api/endpoint/WorkbooksApi';
import { SeeqNames } from '@seeqdev/constants';
import { faSparkles } from '@seeqdev/icons/fa-pro-solid-svg-icons';
import { faClose, faWarning } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Alert, Button, Icon } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { Dispatch, SetStateAction } from 'react';
import type { FormElement } from '@/formbuilder/formBuilder.constants';

import type { ImpactReportCategory, ImpactReportSelectedItemType, Location } from '@/impactReports/impactReport.types';
import type { SavedImpactReportReviewState } from '@/impactReports/impactReport.utilities';
import type { ImpactReportDraft } from '@/impactReports/impactReportForm.utilities';
import type { SelectedSearchItem } from '@/search/search.types';
import type { ScalarPropertyV1 } from '@seeqdev/api/model/ScalarPropertyV1';

interface ImpactReportModalProps {
  existingReport?: string;
  stageIndex?: number;
  workbookId?: string;
  worksheetId?: string;
  selectedItemType: ImpactReportSelectedItemType;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
  setSelectedItemType: Dispatch<SetStateAction<ImpactReportSelectedItemType>>;
  closeFn: (reloadTable?: boolean) => void;
  setStageIndex: (idx: number) => void;
  report?: Record<string, any>;
  categories?: ImpactReportCategory[];
}

interface ImpactReportHeaderProps {
  workbookId: string;
  worksheetId: string;
  selectedItemType: ImpactReportSelectedItemType;
  stageIndex: number;
  setStageIndex: (idx: number) => void;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
  setSelectedItemType: Dispatch<SetStateAction<ImpactReportSelectedItemType>>;
}

const validSuffixes = ['/h', '/d', '/min', '/mo', '/ms', '/ns', '/s', '/wk', '/y', '']; // '' for just the unit itself, e.g. '$'

function isValidUnit(unit: string): boolean {
  return validSuffixes.some((suffix) => unit.endsWith(suffix));
}

const getValidUnitForDisplay = (unit: string) => (unit.indexOf('·') > 0 ? unit.substring(0, unit.indexOf('·')) : unit);

export const ImpactReportHeader = ({
  workbookId,
  worksheetId,
  selectedItemType,
  stageIndex = 0,
  setStageIndex,
  setWorksheetId,
  setWorkbookId,
  setSelectedItemType,
}: ImpactReportHeaderProps) => {
  const { t } = useTranslation();
  const currentStage = availableStages[stageIndex];
  const currentStageDisplay = t(`IMPACT_REPORT.${currentStage?.toUpperCase()}`);
  const dataLabProjectSelected = selectedItemType === SeeqNames.Types.Project;

  return (
    <div className={`${stylex.props(styles.header).className} valueCapture`}>
      <div {...stylex.props(styles.spacedContent)}>
        <h4>{t('IMPACT_REPORT.CAPTURE_IMPACT')}</h4>
      </div>
      <div {...stylex.props(styles.headerRow)}>
        {workbookId && dataLabProjectSelected ? (
          <div {...stylex.props(styles.selectedItem)}>
            <div>{t('IMPACT_REPORT.SELECTED_PROJECT')}</div>
            <div>
              <SelectedProjectDisplayAndLink projectId={workbookId} />
              <Icon
                icon={faClose}
                onClick={() => {
                  setWorkbookId('');
                  setWorksheetId('');
                  setSelectedItemType('');
                  setStageIndex(0);
                }}
                xstyle={styles.clearSelection}
              />
            </div>
          </div>
        ) : workbookId && worksheetId ? (
          <div {...stylex.props(styles.selectedItem)}>
            <div>{t('IMPACT_REPORT.SELECTED_WORKSHEET')}</div>
            <div>
              <SelectedWorksheetDisplayAndLink workbookId={workbookId} worksheetId={worksheetId} />
              <Icon
                icon={faClose}
                onClick={() => {
                  setWorkbookId('');
                  setWorksheetId('');
                  setSelectedItemType('');
                  setStageIndex(0);
                }}
                xstyle={styles.clearSelection}
              />
            </div>
          </div>
        ) : (
          <div {...stylex.props(styles.fill)} />
        )}
        {currentStage && (
          <div {...stylex.props(styles.stages)}>
            <h6 {...stylex.props(styles.currentStage)}>
              {t('IMPACT_REPORT.CURRENT_STAGE')} {currentStageDisplay}
            </h6>
            <ImpactReportSlider setStageIdx={setStageIndex} stageIndex={stageIndex} />
          </div>
        )}
      </div>
    </div>
  );
};

export const ImpactReportForm = ({
  stageIndex = 0,
  workbookId,
  worksheetId,
  selectedItemType,
  setWorkbookId,
  setWorksheetId,
  setSelectedItemType,
  setStageIndex,
  closeFn,
  report,
  categories,
}: ImpactReportModalProps) => {
  const isTopic = useFluxPath(sqWorkbookStore, () => sqWorkbookStore.isTopic);
  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);
  const canReviewReports = hasAnalyticsAdminCapability();
  const kpiColumns = useImpactReportKpiColumns();
  const expectedAnnualKpiColumns = useImpactReportExpectedAnnualKpiColumns();
  const expectedAnnualKpiVisibility = useImpactReportExpectedAnnualKpiVisibility();
  const showSite = useImpactReportShowSite();
  const showLocation = useImpactReportShowLocation();
  const showAssetPath = useImpactReportShowAssetPath();
  const { setUpdateNominations } = useImpactReportActions();
  const { t } = useTranslation();
  const [rowDatumId, setRowDatumId] = useState<string | undefined>(
    report && report[SeeqNames.MaterializedTables.DatumIdColumn],
  );
  const [draft, setDraft] = useState<ImpactReportDraft>(() => ({
    title: '',
    description: genAIEnabled() ? '' : instructions,
    owner: { id: currentUser.id, name: currentUser.name },
    site: currentUser.site ?? '',
    asset: '',
    impactType: 'oneTime',
    // New reports default to today; the dashboard viewing range is a separate concern.
    impactStartDate: moment(),
    impactFrequency: '',
    kpiValues: {},
    expectedAnnualValues: {},
    categoryValues: {},
    reviewComment: '',
  }));
  const updateDraft = (changes: Partial<ImpactReportDraft>) => setDraft((previous) => ({ ...previous, ...changes }));
  const {
    title,
    description,
    owner,
    site,
    asset,
    impactType,
    impactStartDate,
    impactEndDate,
    impactFrequency,
    location,
    kpiValues: customKPIValues,
    expectedAnnualValues: customKPIExpectedAnnualValues,
    categoryValues: customCategories,
    reviewComment,
  } = draft;
  const saveCategories = useImpactReportCategorySave(report, categories);
  const [reviewApprovalConfirmed, setReviewApprovalConfirmed] = useState(false);
  // The report prop is never refreshed while the modal is open, so the review state has to be tracked as it is written.
  const [savedReview, setSavedReview] = useState<SavedImpactReportReviewState>(() =>
    toSavedImpactReportReviewState(report),
  );
  // Undefined until the report has loaded; thereafter what the row holds, including saves made in this session.
  const [savedFieldValues, setSavedFieldValues] = useState<ReturnType<typeof serializeImpactReportDraft>>();
  const [forceUpdateText, setForceUpdateText] = useState(description);
  const dataLabProjectSelected = selectedItemType === SeeqNames.Types.Project;
  const isVantage = selectedItemType === SeeqNames.Types.Vantage;
  const [hasReadPermissions, setHasReadPermissions] = useState(false);
  const [savedReadPermissions, setSavedReadPermissions] = useState(false);

  const impactFrequencyOptions = [
    { value: DAILY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.DAILY') },
    { value: WEEKLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.WEEKLY') },
    { value: MONTHLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.MONTHLY') },
    { value: QUARTERLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.QUARTERLY') },
    { value: YEARLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.YEARLY') },
  ];

  useAsyncEffect(async () => {
    if (!workbookId) return;
    const everyoneCanRead = await verifyEveryoneGroupReadPermissions(workbookId);
    setHasReadPermissions(everyoneCanRead);
    setSavedReadPermissions(everyoneCanRead);
  }, [workbookId]);

  useAsyncEffect(async () => {
    if (!workbookId || selectedItemType) return;
    const workbookResponse = await sqWorkbooksApi.getWorkbook({ id: workbookId });
    setSelectedItemType(toImpactReportSelectedItemType(workbookResponse.data?.type));
  }, [workbookId, selectedItemType]);

  const [showAIOverlay, setShowAIOverlay] = useState(description === '');
  const tableId = useImpactReportTableId();
  const { loadImpactReportTableId } = useImpactReportApiActions();
  const supportsAI = Boolean(workbookId && worksheetId && !isVantage && !dataLabProjectSelected);

  useAsyncEffect(async () => {
    await loadImpactReportTableId();
  }, []);

  useEffect(() => {
    setShowAIOverlay(supportsAI && description === '');
    if (!supportsAI && workbookId && !description) {
      setForceUpdateText(instructions);
    }
  }, [supportsAI, workbookId, description]);

  useEffect(() => {
    if (!report || _.isEmpty(report)) return;
    const loaded = loadImpactReportDraft(report, kpiColumns, expectedAnnualKpiColumns, categories);
    setDraft(loaded);
    setSavedFieldValues(serializeImpactReportDraft(loaded));
    setSavedReview(toSavedImpactReportReviewState(report));
    setWorksheetId(loaded.worksheetId ?? '');
    setWorkbookId(loaded.workbookId ?? '');
    setSelectedItemType(toImpactReportSelectedItemType(report[SeeqNames.ImpactReport.ImpactBasedOn]));
  }, [categories, expectedAnnualKpiColumns, kpiColumns, report, setSelectedItemType, setWorkbookId, setWorksheetId]);

  const currentStage = availableStages[stageIndex];
  const fieldValues = serializeImpactReportDraft({ ...draft, workbookId, worksheetId });
  // Until the report has loaded there is nothing to compare against, and a new report has no review to lose either way.
  const fieldsChanged = Boolean(savedFieldValues) && !_.isEqual(fieldValues, savedFieldValues);
  const changedSinceSave =
    fieldsChanged ||
    reviewComment !== (savedReview.review?.comment ?? '') ||
    savedReadPermissions !== hasReadPermissions ||
    savedReview.status !== currentStage;
  // Resolved per call so that a review is stamped with the time of the save that granted it.
  const resolveReviewSave = () =>
    resolveImpactReportReviewSave({
      canReviewReports,
      currentStage,
      saved: savedReview,
      reviewApprovalConfirmed,
      changedSinceSave,
      reviewComment,
      reviewedBy: currentUser.id,
      reviewDate: moment().toISOString(),
    });
  const willRevertReviewToQuantify = savedReview.status === 'review' && resolveReviewSave().status !== 'review';

  useEffect(() => {
    if (currentStage === 'review' || !reviewApprovalConfirmed) return;
    setReviewApprovalConfirmed(false);
  }, [currentStage, reviewApprovalConfirmed]);

  const sectionHeader = (stageIndex: number) => (
    <>
      <div {...stylex.props(styles.sectionHeader, stageIndex !== 0 && styles.laterSection)}>
        <Icon icon={stageIcons[stageIndex]} xstyle={styles.sectionIcon} />
        <h4 {...stylex.props(styles.fill)}>{stageDisplayStringByIndex(stageIndex)}</h4>
      </div>
      <hr />
    </>
  );

  if (!tableId) return null;

  const expectedAnnualColumnNames = new Set(expectedAnnualKpiColumns.map((column) => column.columnName));

  const kpiFields = kpiColumns
    ? kpiColumns.map((kpi) => {
        const value = customKPIValues[kpi.columnName];
        const expectedAnnualColumnName = getKPIExpectedAnnualColumnName(kpi.columnName);
        const includeExpectedAnnual =
          expectedAnnualKpiVisibility[kpi.columnName] && expectedAnnualColumnNames.has(expectedAnnualColumnName);
        const expectedAnnualValue = customKPIExpectedAnnualValues[expectedAnnualColumnName];
        const unit = getKPIUnit(kpi.columnName);
        const valueRowComponents: FormElement[] = [
          {
            component: 'ToggleInputSelectItemSearchFormComponent' as const,
            label: t('IMPACT_REPORT.PROVIDED_VALUE'),
            name: kpi.columnName,
            onChange: (item: string | SelectedSearchItem[] | number | null) => {
              if (typeof item === 'string' || typeof item === 'number') {
                updateDraft({ kpiValues: { ...customKPIValues, [kpi.columnName]: { mode: 'manual', amount: item } } });
              } else if (!item?.length) {
                updateDraft({ kpiValues: { ...customKPIValues, [kpi.columnName]: { mode: 'calculation' } } });
              } else if (item.length === 1) {
                const selectedItem = item[0];
                // verify the units - if they are invalid we have to make sure we let the user know or they can end
                // up with a very broken table :(
                const itemUnit =
                  'valueUnitOfMeasure' in selectedItem && typeof selectedItem.valueUnitOfMeasure === 'string'
                    ? selectedItem.valueUnitOfMeasure
                    : undefined;
                const kpiUnit = getKPIUnit(kpi.columnName);
                if (
                  (kpiUnit.indexOf('·') > -1 && itemUnit === kpiUnit.replace('·', '/')) ||
                  itemUnit === kpiUnit.substring(0, kpiUnit.indexOf('·')) ||
                  (itemUnit?.startsWith(kpiUnit) && isValidUnit(itemUnit))
                ) {
                  updateDraft({
                    kpiValues: {
                      ...customKPIValues,
                      [kpi.columnName]: { mode: 'calculation', item: { id: selectedItem.id } },
                    },
                  });
                  return;
                }
                errorToast({
                  messageKey: 'IMPACT_REPORT.INVALID_UNIT',
                  messageParams: { unit: getKPIUnit(kpi.columnName) },
                });
              }
            },
            unit: _.find(impactFrequencyOptions, { value: impactFrequency })?.label,
            required: false,
            showItemSelection: value?.mode === 'calculation',
            setShowItemSelection: (columnName: string, shouldShowSelection: boolean) => {
              updateDraft({
                kpiValues: {
                  ...customKPIValues,
                  [columnName]: changeImpactReportKpiMode(
                    customKPIValues[columnName],
                    shouldShowSelection ? 'calculation' : 'manual',
                  ),
                },
              });
            },
            size: 'sm' as const,
            testId: kpi.columnName,
            value: value?.mode === 'calculation' ? (value.item ? [value.item] : []) : (value?.amount ?? ''),
            isMultiple: false,
            notSelectedMessage: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
            title: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
            overrideWorkbookId: workbookId,
            scopeIds: [workbookId],
            searchTypes: ['Signal'],
            showToggle: impactType !== 'oneTime',
            iconPartialTooltipKey: 'IMPACT_REPORT.CUSTOM_CALC_TOOLTIP',
            customCalcInstructions: t('IMPACT_REPORT.CUSTOM_CALC_INSTRUCTIONS', {
              unit: getValidUnitForDisplay(getKPIUnit(kpi.columnName)),
            }),
          } as FormElement,
        ];

        if (includeExpectedAnnual) {
          valueRowComponents.push({
            component: 'TextFieldFormComponent',
            label: t('IMPACT_REPORT.EXPECTED_ANNUAL_SAVINGS'),
            name: expectedAnnualColumnName,
            value: expectedAnnualValue ?? '',
            onChange: (newValue) => {
              updateDraft({
                expectedAnnualValues: {
                  ...customKPIExpectedAnnualValues,
                  [expectedAnnualColumnName]: newValue as string,
                },
              });
            },
            size: 'sm',
            type: 'number',
            min: 0,
            testId: expectedAnnualColumnName,
            required: false,
            wrapperClassNames: 'width-30percent ml10',
          } as FormElement);
        }

        const components: FormElement[] = [
          {
            component: 'DisplayOnlyFormElementWrapper',
            name: `${kpi.columnName}-header`,
            children: (
              <div {...stylex.props(styles.spacedContent)}>
                <KpiLabelWithDescription
                  label={`${getKPIDisplayName(kpi.columnName)} (${unit})`}
                  description={kpi.description}
                />
              </div>
            ),
          },
          {
            component: 'FormRow',
            name: `${kpi.columnName}-values`,
            components: valueRowComponents,
          } as FormElement,
        ];

        return {
          component: 'FormGroup',
          name: `${kpi.columnName}-group`,
          formGroupExtraClassNames: 'flexColumnContainer p10 simpleBorder grayBorder roundedBorder mb10',
          components,
        } as FormElement;
      })
    : [];

  const categoryFields = categories
    ? categories.map((category) => {
        const cat = category.category;
        const fieldName = getCustomCategoryTitle(category);
        const options = category.options;
        const categoryId = cat.id!;
        return {
          label: fieldName,
          component: 'SelectFormComponent',
          name: categoryId,
          value: customCategories[categoryId],
          required: false,
          options: options as any,
          onChange: (value) => {
            updateDraft({
              categoryValues: { ...customCategories, [categoryId]: typeof value === 'string' ? value : undefined },
            });
          },
        } as FormElement;
      })
    : [];

  const updateCKContent = (content: string) => {
    updateDraft({ description: description + content });
    setForceUpdateText(description + content);
  };

  const formDefinition: FormElement[] = [
    {
      component: 'FormGroup',
      name: 'identify',
      includeIf: stageIndex >= 0,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'identifyHeader',
          children: sectionHeader(0),
        },
        {
          component: 'TextFieldFormComponent',
          name: 'title',
          label: 'Title',
          value: title,
          required: true,
          onChange: (title) => {
            updateDraft({ title: title as string });
          },
          size: 'sm',
          testId: 'formTitle',
        },
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'CKEditorLabel',
          children: [
            <div {...stylex.props(styles.descriptionLabel)} key="CKLabel">
              {t('IMPACT_REPORT.DESCRIPTION')}
              {genAIEnabled() && !showAIOverlay && worksheetId && workbookId && !isVantage && (
                <Icon
                  onClick={() => setShowAIOverlay(true)}
                  icon={faSparkles}

                  xstyle={styles.descriptionIcon}
                />
              )}
            </div>,
          ],
        },
        {
          component: 'CKEditorFormComponent',
          name: 'description',
          label: '',
          extraClassNames: stylex.props(styles.fullWidth).className,
          value: description,
          required: true,
          onChange: (description) => {
            updateDraft({ description: description as string });
          },
          testId: 'description',
          forceUpdateText,
          overlay:
            genAIEnabled() && showAIOverlay && supportsAI ? (
              <ImpactReportAIOverlay
                onClose={() => setShowAIOverlay(false)}
                updateCKContent={updateCKContent}
                worksheetId={worksheetId}
                workbookId={workbookId}
              />
            ) : undefined,
          placeholder: '',
        },
        {
          component: 'SelectIdentityFormComponent',
          name: 'owner',
          idForLabel: 'useCaseOwner',
          label: 'Use case owner',
          extraClassNames: stylex.props(styles.owner).className,
          value: owner.id ? owner : [],
          required: true,
          startEditable: true,
          onChange: (selectedIdentity) => {
            if (!Array.isArray(selectedIdentity)) {
              updateDraft({ owner: { id: selectedIdentity.id ?? '', name: selectedIdentity.name ?? '' } });
            }
          },
          testId: 'owner',
        },
        {
          component: 'CheckboxFormComponent',
          id: 'grantReadPermissions',
          name: 'grantReadPermissions',
          label: '',
          onChange: async () => {
            setHasReadPermissions(!hasReadPermissions);
          },
          skipToFormState: true,
          checkboxLabel: t('IMPACT_REPORT.READ_PERMISSIONS_REQUIRED'),
          value: hasReadPermissions,
        },
      ],
    },

    {
      component: 'FormGroup',
      name: 'categorize',
      includeIf: stageIndex >= 1,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'categoryHeader',
          children: sectionHeader(1),
        },
        {
          component: 'LabelFormComponent',
          name: 'locationLabel',
          value: 'Location',
          includeIf: showLocation,
        },
        {
          component: 'LocationLookupFormComponent',
          name: 'locationLookup',
          extraClassNames: stylex.props(styles.location).className,
          label: 'Location',
          value: location,
          onChange: (location: Location) => {
            updateDraft({ location });
          },
          testId: 'location',
          required: false,
          includeIf: showLocation,
        },
        {
          component: 'TextFieldFormComponent',
          name: 'site',
          label: 'Site',
          value: site,
          onChange: (site) => {
            updateDraft({ site: site as string });
          },
          size: 'sm',
          testId: 'site',
          includeIf: showSite,
        },
        ...categoryFields,
        {
          component: 'TextFieldFormComponent',
          name: 'asset',
          label: 'Asset type',
          value: asset,
          onChange: (asset) => {
            updateDraft({ asset: asset as string });
          },
          size: 'sm',
          testId: 'assetType',
          includeIf: showAssetPath,
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'quantify',
      includeIf: stageIndex >= 2,

      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'quantifyHeader',
          children: sectionHeader(2),
        },
        {
          component: 'LabelFormComponent',
          name: 'impactType',
          value: 'Impact type',
          extraClassNames: '',
        },
        {
          component: 'FormRow',
          name: 'justARow',
          components: [
            {
              component: 'CheckboxFormComponent',
              name: 'oneTime',
              type: 'radio',
              value: impactType === 'oneTime',
              onChange: () => {
                updateDraft({
                  impactType: 'oneTime',
                  impactFrequency: '',
                  kpiValues: Object.fromEntries(
                    Object.entries(customKPIValues).map(([column, value]) => [
                      column,
                      changeImpactReportKpiMode(value, 'manual'),
                    ]),
                  ),
                });
              },
              checkboxLabel: 'One time impact',
              id: 'oneTime',
              testId: 'oneTime',
              skipToFormState: true,
              classes: 'flexAlignCenter mr20',
            },
            {
              component: 'CheckboxFormComponent',
              name: 'Continuous',
              type: 'radio',
              value: impactType === 'continuous',
              onChange: () => {
                updateDraft({ impactType: 'continuous' });
              },
              checkboxLabel: 'continuous impact',
              id: 'continuous',
              testId: 'continuous',
              classes: 'flexAlignCenter mr20',
            },
          ],
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'startEndDateRow',
      includeIf: stageIndex >= 2,
      components: [
        {
          includeIf: impactType !== 'customCalc',
          component: 'FormRow',
          name: 'startEndRow',
          extraClassNames: stylex.props(styles.centered).className,
          components: [
            {
              component: 'DatePickerFormComponent',
              name: 'impactStartDate',
              label: t('IMPACT_REPORT.START_DATE'),
              value: impactStartDate,
              required: true,
              onChange: (date) => {
                updateDraft({ impactStartDate: date });
              },
              testId: 'impactStartDate',
              wrapperClassNames: 'mr5',
            },
            {
              component: 'DatePickerFormComponent',
              name: 'impactEndDate',
              includeIf: impactType === 'continuous',
              label: t('IMPACT_REPORT.END_DATE'),
              value: impactEndDate,
              onChange: (date) => {
                updateDraft({ impactEndDate: date });
              },
              testId: 'impactEndDate',
              wrapperClassNames: 'ml15',
            },
            {
              component: 'FormGroup',
              name: 'continuousImpact',
              includeIf: impactType === 'continuous',
              formGroupExtraClassNames: 'ml20 max-width-150',
              components: [
                {
                  label: t('IMPACT_REPORT.FREQUENCY'),
                  component: 'SelectFormComponent',
                  name: 'impactFrequency',
                  value: impactFrequency,
                  options: impactFrequencyOptions,
                  required: impactType === 'continuous',
                  onChange: (frequency) => {
                    updateDraft({ impactFrequency: frequency as string });
                  },
                },
              ],
            },
          ],
        },
        {
          component: 'FormRow',
          name: 'monetizeFormRow',
          components: [
            {
              component: 'FormGroup',
              name: 'kpis',
              components: [...kpiFields],
            },
          ],
        },
      ],
    },

    {
      component: 'DisplayOnlyFormElementWrapper',
      name: 'reviewedReportEditWarning',
      includeIf: willRevertReviewToQuantify,
      children: (
        <Alert dismissible={false} variant="warning" testId="reviewedReportEditWarning" xstyle={styles.reviewWarning}>
          <span>
            <Icon icon={faWarning} xstyle={styles.warningIcon} type="warning" tooltipPlacement="right" />{' '}
            {t('IMPACT_REPORT.EDIT_REVERTS_REVIEW')}
            {/* Only an admin has the toggle, and only on the Review stage where it is rendered below this warning. */}
            {canReviewReports && currentStage === 'review' && ` ${t('IMPACT_REPORT.EDIT_REVERTS_REVIEW_TOGGLE_HINT')}`}
          </span>
        </Alert>
      ),
    },
    {
      component: 'FormGroup',
      name: 'review',
      includeIf: currentStage === 'review',
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'reviewHeader',
          children: sectionHeader(3),
        },
        {
          component: 'TextFieldFormComponent',
          name: 'reviewComment',
          label: t('IMPACT_REPORT.REVIEW_COMMENT'),
          value: reviewComment,
          size: 'sm',
          as: 'textarea',
          rows: 4,
          onChange: (comment) => {
            updateDraft({ reviewComment: comment as string });
          },
          testId: 'reviewComment',
        },
        {
          component: 'ToggleFormComponent',
          name: 'reviewApprovalConfirmed',
          value: reviewApprovalConfirmed,
          onChange: () => {
            setReviewApprovalConfirmed((prev) => !prev);
          },
          iconSize: '2x',
          title: t('IMPACT_REPORT.REVIEW_APPROVAL_TOGGLE_TITLE'),
          label: t('IMPACT_REPORT.REVIEW_APPROVAL_TOGGLE_DESCRIPTION'),
          labelPosition: 'right',
          testId: 'reviewApprovalConfirmation',
        },
      ],
    },
  ];

  const onFormSubmitCallback = async () => {
    const properties: ScalarPropertyV1[] = Object.entries(fieldValues.properties).map(([name, value]) => ({
      name,
      value,
    }));
    const reviewSave = resolveReviewSave();

    const isContinuous = impactType === 'continuous';
    const hasManualSelection = Object.values(customKPIValues).some(
      (value) => value?.mode === 'manual' && value.amount !== '',
    );
    const unit = _.find(impactFrequencyOptions, { value: impactFrequency })?.label;
    if (isContinuous && hasManualSelection && !unit) {
      errorToast({
        messageKey: 'IMPACT_REPORT.FREQUENCY_REQUIRED',
        messageParams: { unit },
      });
      return Promise.reject();
    }
    // if we are updating an existing report, we need to use the existing datum id
    let useThisDatumId = rowDatumId;
    if (useThisDatumId === undefined) {
      doTrack(TRACK_IMPACT, 'Created new Impact Report', { isTopic, impactType });
      useThisDatumId = randomUUID();
      // Reserve the identity before either request so a retry continues the same report.
      setRowDatumId(useThisDatumId);
    } else {
      doTrack(TRACK_IMPACT, 'Updated existing Impact Report', { isTopic, impactType });
    }

    properties.push({ name: SeeqNames.MaterializedTables.ItemIdColumn, value: tableId });
    properties.push({ name: SeeqNames.MaterializedTables.DatumIdColumn, value: useThisDatumId });

    // CRAB-46894: Keep the legacy row identifier until the Impact Report table's datum-id rules are migrated.
    properties.push({ name: SeeqNames.MaterializedTables.UniqueRowId, value: useThisDatumId });

    await saveCategories(tableId, useThisDatumId, fieldValues.categoryValues, async () => {
      if (savedReview.status !== 'review') return;
      await addOrUpdateImpactReport(tableId, withImpactReportReview(properties, { status: 'quantify' }));
      // This transition survives a category or final-publication failure and must also survive a retry.
      setSavedReview({ status: 'quantify' });
    });

    await addOrUpdateImpactReport(tableId, withImpactReportReview(properties, reviewSave));
    setRowDatumId(useThisDatumId); // ensure this gets set so the "continue" button works
    setSavedReview(reviewSave);
    setSavedFieldValues(fieldValues);
    updateDraft({ reviewComment: reviewSave.review?.comment ?? '' });

    if (savedReadPermissions !== hasReadPermissions) {
      if (hasReadPermissions) {
        await giveEveryoneGroupReadPermissions(workbookId!);
      } else {
        await removeEveryoneGroupReadPermissions(workbookId!);
      }
      setSavedReadPermissions(hasReadPermissions);
    }
  };

  return (
    <div className={`${stylex.props(styles.root).className} valueCapture`}>
      {!workbookId && !worksheetId && (
        <div {...stylex.props(styles.workbookSelector)}>
          <div {...stylex.props(styles.selectionHeader)}>
            <h4 {...stylex.props(styles.selectionTitle)}>{t('IMPACT_REPORT.SELECT_WORKSHEET_AND_WORKBOOK')}</h4>
          </div>
          <hr />
          <ReportContentWorksheetAndUrl
            selectOnlyWorkbooks={true}
            allowedTypes={[
              SeeqNames.Types.Topic,
              SeeqNames.Types.Analysis,
              SeeqNames.Types.Vantage,
              SeeqNames.Types.Folder,
              SeeqNames.Types.Project,
            ]}
            extraClassNames={stylex.props(styles.fullWidth).className}
            setSelectedItem={(item) => {
              setSelectedItemType(toImpactReportSelectedItemType(item?.type));
              setWorkbookId(item?.id ?? '');
            }}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!workbookId} xstyle={styles.continueButton} />
        </div>
      )}
      {workbookId && !worksheetId && !dataLabProjectSelected && (
        <div {...stylex.props(styles.worksheetSelector)}>
          <div {...stylex.props(styles.selectionHeader)}>
            <h4 {...stylex.props(styles.selectionTitle)}>{t('IMPACT_REPORT.SELECT_WORKSHEET')}</h4>
          </div>
          <hr />
          <SelectWorksheet
            workbookId={workbookId}
            worksheetIds={[]}
            setWorksheetIds={(worksheetIds) => {
              if (worksheetIds?.length > 0) setWorksheetId(worksheetIds[0]);
            }}
            workstepIds={[]}
            setWorkstepIds={() => {}}
            setTotalWorksheets={() => {}}
            isLoading={false}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!worksheetId} xstyle={styles.continueButton} />
        </div>
      )}

      {workbookId && (worksheetId || dataLabProjectSelected) && (
        <SaveAndContinueFormBuilder
          submitBtnLabel={t('IMPACT_REPORT.SAVE_AND_CLOSE')}
          submitBtnId="saveAndClose"
          extraClassNames={stylex.props(styles.form).className}
          continueButtonLabel={t('IMPACT_REPORT.SAVE_AND_CONTINUE')}
          continueButtonPosition="center"
          continueButtonExtraClassNames={stylex.props(styles.continueSpacing).className}
          continueButtonEnabled={
            stageIndex < availableStages.length - 1 &&
            (availableStages[stageIndex + 1] !== 'review' || canReviewReports)
          }
          continueButtonOnSubmit={async () => {
            await onFormSubmitCallback();
            if (willRevertReviewToQuantify) {
              setStageIndex(availableStages.indexOf('quantify'));
            } else if (
              stageIndex < availableStages.length - 1 &&
              (availableStages[stageIndex + 1] !== 'review' || canReviewReports)
            ) {
              setStageIndex(stageIndex + 1);
            }
            doTrack(TRACK_IMPACT, 'Save and Continue Button', {
              stageIndex,
              changedSinceSave,
              willRevertReviewToQuantify,
            });
          }}
          continueButtonVariant="theme"
          formDefinition={formDefinition}
          submitFn={async () => {
            await onFormSubmitCallback();
            setRowDatumId(undefined);
            closeFn(true);
            setUpdateNominations(true);
            doTrack(TRACK_IMPACT, 'Save and Close Button', {
              stageIndex,
              changedSinceSave,
              willRevertReviewToQuantify,
            });
          }}
          closeFn={closeFn}
        />
      )}
    </div>
  );
};

const styles = stylex.create({
  header: { display: 'flex', flexDirection: 'column', width: '100%' },
  spacedContent: { marginBottom: 5 },
  headerRow: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap' },
  selectedItem: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
    marginTop: 0,
    alignItems: 'flex-start',
  },
  fill: { flex: '1 1 auto', overflow: 'hidden' },
  stages: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  currentStage: { marginTop: -8 },
  descriptionLabel: { display: 'flex', flexDirection: 'row', marginBottom: 5, alignItems: 'center' },
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
    marginBottom: 10,
    alignItems: 'center',
  },
  workbookSelector: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
    width: '100%',
    marginTop: 15,
    alignItems: 'center',
  },
  worksheetSelector: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
    marginTop: 15,
    alignItems: 'center',
  },
  selectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    height: 30,
    width: '100%',
    marginBottom: 10,
    alignItems: 'center',
    backgroundColor: colors.surfaceSubtle,
  },
  selectionTitle: { marginLeft: 10 },
  clearSelection: { marginLeft: 10, marginTop: 3, cursor: 'pointer' },
  sectionIcon: { marginRight: 10, marginLeft: 5 },
  descriptionIcon: { marginLeft: 5, cursor: 'pointer' },
  reviewWarning: { marginTop: '0.5rem', marginBottom: '0.5rem' },
  warningIcon: { margin: 3 },
  continueButton: { marginTop: 10 },
  fullWidth: { width: '100%' },
  owner: { width: '100%', marginBottom: 25 },
  location: { marginBottom: 8 },
  centered: { alignItems: 'center' },
  form: { flex: '1 1 auto', overflow: 'auto', height: '100%' },
  continueSpacing: { marginRight: 10 },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: -16,
    height: 30,
    alignItems: 'center',
    backgroundColor: colors.surfaceSubtle,
  },
  laterSection: { marginTop: '1.25rem' },
});
